/**
 * Gossl Theme - Collection Filters
 * Instant-apply sidebar filters, sorting, accordion groups, mobile slide-in.
 */

(function() {
  'use strict';

  class CollectionFilters {
    constructor() {
      this.sidebar = document.querySelector('[data-collection-sidebar]');
      this.sidebarToggle = document.querySelector('[data-sidebar-toggle]');
      this.sidebarClose = document.querySelector('[data-sidebar-close]');
      this.backdrop = document.querySelector('[data-sidebar-backdrop]');
      this.filterForm = document.querySelector('[data-filter-form]');
      this.sortSelect = document.querySelector('[data-sort-select]');
      this.filterGroups = document.querySelectorAll('[data-filter-group]');
      this.priceDebounce = null;

      this.bindEvents();
    }

    bindEvents() {
      // Mobile sidebar
      if (this.sidebarToggle) this.sidebarToggle.addEventListener('click', () => this.openSidebar());
      if (this.sidebarClose) this.sidebarClose.addEventListener('click', () => this.closeSidebar());
      if (this.backdrop) this.backdrop.addEventListener('click', () => this.closeSidebar());

      // Sort — uses unified navigate so filter params are preserved
      if (this.sortSelect) {
        this.sortSelect.addEventListener('change', () => this.navigate());
      }

      // Form submit fallback
      if (this.filterForm) {
        this.filterForm.addEventListener('submit', (e) => { e.preventDefault(); this.navigate(); });
      }

      // Checkbox filters — instant apply
      this.filterForm?.querySelectorAll('[data-filter-checkbox]').forEach(cb => {
        cb.addEventListener('change', () => {
          const pill = cb.closest('.goessl-filter__size-pill');
          if (pill) pill.classList.toggle('is-active', cb.checked);
          this.navigate();
        });
      });

      // Radio filters — click-to-deselect + instant apply
      const radios = this.filterForm?.querySelectorAll('[data-filter-radio]') || [];
      radios.forEach(radio => {
        radio.addEventListener('click', () => {
          if (radio.dataset.wasChecked === 'true') {
            radio.checked = false;
            radio.dataset.wasChecked = 'false';
          } else {
            radios.forEach(r => { r.dataset.wasChecked = 'false'; });
            radio.dataset.wasChecked = 'true';
          }
          this.navigate();
        });
        if (radio.checked) radio.dataset.wasChecked = 'true';
      });

      // Color swatch buttons — toggle active + navigate
      const colorSwatches = this.filterForm?.querySelectorAll('[data-filter-color]') || [];
      colorSwatches.forEach(swatch => {
        swatch.addEventListener('click', () => {
          const isActive = swatch.classList.contains('active');
          // Deactivate all, then toggle this one
          colorSwatches.forEach(s => s.classList.remove('active'));
          if (!isActive) {
            swatch.classList.add('active');
          }
          this.navigate();
        });
      });

      // Price range — debounced
      this.initPriceSlider();

      // Accordion — only one open at a time (except price)
      this.initAccordion();

      // Merge duplicate sizes (e.g. "42", "42 (LEDERHOSEN)" → single "42" pill)
      this.mergeDuplicateSizes();

      // Escape to close
      document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape' && this.sidebar?.classList.contains('is-open')) this.closeSidebar();
      });
    }

    mergeDuplicateSizes() {
      const self = this;
      document.querySelectorAll('.goessl-filter__size-grid').forEach(function(grid) {
        const pills = Array.from(grid.querySelectorAll('.goessl-filter__size-pill'));
        if (!pills.length) return;

        // Group pills by base size (strip parenthetical)
        const groups = {};
        const order = [];
        pills.forEach(function(pill) {
          const label = pill.querySelector('span');
          if (!label) return;
          const text = label.textContent.trim();
          const base = text.replace(/\s*\(.*\)$/, '').trim();
          if (!groups[base]) { groups[base] = []; order.push(base); }
          groups[base].push({ pill: pill, label: label, original: text });
        });

        order.forEach(function(base) {
          const items = groups[base];

          // Strip parenthetical from display even for single items
          items[0].label.textContent = base;

          if (items.length <= 1) return;

          // Multiple pills for same base — merge
          const primary = items[0];
          let isAnyActive = false;
          let isAllUnavailable = true;
          const allInputs = [];

          items.forEach(function(item) {
            const input = item.pill.querySelector('input');
            if (input) allInputs.push(input);
            if (item.pill.classList.contains('is-active')) isAnyActive = true;
            if (!item.pill.classList.contains('is-unavailable')) isAllUnavailable = false;
          });

          if (isAnyActive) primary.pill.classList.add('is-active');
          if (isAllUnavailable) primary.pill.classList.add('is-unavailable');
          else primary.pill.classList.remove('is-unavailable');

          // Hide duplicates
          for (let i = 1; i < items.length; i++) {
            items[i].pill.style.display = 'none';
          }

          // Click toggles ALL inputs for this base size, then navigates
          const primaryInput = primary.pill.querySelector('input');
          if (primaryInput) {
            primaryInput.removeEventListener('change', primaryInput._mergeHandler);
            primaryInput._mergeHandler = function() {
              const checked = primaryInput.checked;
              allInputs.forEach(function(inp) { inp.checked = checked; });
              self.navigate();
            };
            primaryInput.addEventListener('change', primaryInput._mergeHandler);
          }
        });
      });
    }

    initAccordion() {
      const self = this;
      this.filterGroups.forEach(function(group) {
        const summary = group.querySelector('summary');
        const content = group.querySelector('.goessl-sidebar__group-content');
        if (!summary || !content) return;

        // Prevent native toggle — we handle it manually
        summary.addEventListener('click', function(e) {
          e.preventDefault();
          const isPrice = !!group.querySelector('[data-price-slider]');

          if (group.open) {
            // Closing
            self.animateClose(group, content);
          } else {
            // Close others first (skip price)
            if (!isPrice) {
              self.filterGroups.forEach(function(other) {
                if (other === group || !other.open) return;
                if (other.querySelector('[data-price-slider]')) return;
                const otherContent = other.querySelector('.goessl-sidebar__group-content');
                if (otherContent) self.animateClose(other, otherContent);
              });
            }
            // Open this one
            self.animateOpen(group, content);
          }
        });
      });
    }

    animateOpen(details, content) {
      details.open = true;
      content.style.overflow = 'hidden';
      content.style.height = '0';
      const targetH = content.scrollHeight + 'px';
      // Force reflow
      content.offsetHeight;
      content.style.transition = 'height 0.25s ease';
      content.style.height = targetH;
      content.addEventListener('transitionend', function handler() {
        content.removeEventListener('transitionend', handler);
        content.style.height = '';
        content.style.overflow = '';
        content.style.transition = '';
      });
    }

    animateClose(details, content) {
      content.style.overflow = 'hidden';
      content.style.height = content.scrollHeight + 'px';
      content.offsetHeight;
      content.style.transition = 'height 0.25s ease';
      content.style.height = '0';
      content.addEventListener('transitionend', function handler() {
        content.removeEventListener('transitionend', handler);
        details.open = false;
        content.style.height = '';
        content.style.overflow = '';
        content.style.transition = '';
      });
    }

    openSidebar() {
      if (!this.sidebar) return;
      this.sidebar.style.visibility = 'visible';
      this.sidebar.classList.add('is-open');
      this.backdrop?.classList.add('is-visible');
      this.sidebarToggle?.setAttribute('aria-expanded', 'true');
      document.body.style.overflow = 'hidden';
    }

    closeSidebar() {
      if (!this.sidebar) return;
      this.sidebar.classList.remove('is-open');
      this.backdrop?.classList.remove('is-visible');
      this.sidebarToggle?.setAttribute('aria-expanded', 'false');
      document.body.style.overflow = '';
      this.sidebarToggle?.focus();
    }

    initPriceSlider() {
      const container = document.querySelector('[data-price-slider]');
      if (!container) return;

      const self = this;
      const sliderMin = container.querySelector('[data-slider-min]');
      const sliderMax = container.querySelector('[data-slider-max]');
      const fill = container.querySelector('[data-slider-fill]');
      const labelMin = container.querySelector('[data-slider-label-min]');
      const labelMax = container.querySelector('[data-slider-label-max]');
      const hiddenMin = container.querySelector('[data-filter-price-min]');
      const hiddenMax = container.querySelector('[data-filter-price-max]');
      const rangeMax = parseInt(sliderMin.max);

      if (parseInt(sliderMax.value) === 0) sliderMax.value = rangeMax;

      const updateSlider = function(shouldApply) {
        let min = parseInt(sliderMin.value);
        let max = parseInt(sliderMax.value);
        if (min > max) { const tmp = min; min = max; max = tmp; }

        fill.style.left = (min / rangeMax * 100) + '%';
        fill.style.width = ((max - min) / rangeMax * 100) + '%';
        labelMin.textContent = min + ' €';
        labelMax.textContent = max + ' €';
        hiddenMin.value = min > 0 ? min : '';
        hiddenMax.value = max < rangeMax ? max : '';

        if (shouldApply) {
          clearTimeout(self.priceDebounce);
          self.priceDebounce = setTimeout(function() { self.navigate(); }, 600);
        }
      };

      sliderMin.addEventListener('input', function() { updateSlider(true); });
      sliderMax.addEventListener('input', function() { updateSlider(true); });
      updateSlider(false);
    }

    /**
     * Unified navigation: builds URL from current filter form + sort select.
     * Preserves both filter and sort state on every navigation.
     */
    navigate() {
      const url = new URL(window.location.href);

      // Clear existing filter params
      const keysToDelete = [];
      url.searchParams.forEach(function(v, k) {
        if (k.startsWith('filter.')) keysToDelete.push(k);
      });
      keysToDelete.forEach(function(k) { url.searchParams.delete(k); });

      // Also clear any non-filter param that the form will re-set
      // (so `q` on the search page doesn't get duplicated each navigate)
      if (this.filterForm) {
        const formKeys = new Set();
        new FormData(this.filterForm).forEach(function(v, k) { formKeys.add(k); });
        formKeys.forEach(function(k) {
          if (!k.startsWith('filter.')) url.searchParams.delete(k);
        });
      }

      // Add filter params from form
      if (this.filterForm) {
        const formData = new FormData(this.filterForm);
        formData.forEach(function(value, key) {
          if (value) url.searchParams.append(key, value);
        });

        // Add active color swatch (button-based, not in FormData)
        const activeColor = this.filterForm.querySelector('[data-filter-color].active');
        if (activeColor) {
          url.searchParams.append(activeColor.dataset.paramName, activeColor.dataset.paramValue);
        }
      }

      // Set sort
      if (this.sortSelect) {
        url.searchParams.set('sort_by', this.sortSelect.value);
      }

      // Reset to page 1
      url.searchParams.delete('page');

      window.location.href = url.toString();
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function() { new CollectionFilters(); });
  } else {
    new CollectionFilters();
  }
})();
